import { apiClient, READ_RETRIES } from "~/api/client";
import type { OrderData, OrderStatus } from "~/types/order";
import type { Addresses } from "~/types/user";

// ============================================================================
// Core Order Types
// ============================================================================

export type OrderVideoUploadStatus = "no_order" | "orders_waiting_video" | "all_orders_have_video";

// ============================================================================
// Request/Response Types
// ============================================================================

export interface EmptyRequest {
  // Empty request body
}

export interface OrderVideoUploadStatusResponse {
  status: OrderVideoUploadStatus;
}

export interface ChangeToNextStatusRequest {
  orderId: string;
}

export interface ChangeToNextStatusResponse {
  order: Record<string, any>;
}

export interface FillAddressesRequest {
  orderId?: string;
  addresses: Addresses;
}

export interface FillAddressesResponse {
  orderShippingAddressFilled: boolean;
  userDefaultAddressFilled: boolean;
  editedOrderIds: string[];
}

export interface RequireFillAddressesRequest {
  completedDuoDealOnly: boolean;
}

export interface RequireFillAddressesResponse {
  orderIds: string[];
}

export interface OrderHistoryRequest {
  filters?: {
    status?: OrderStatus;
    statusList?: OrderStatus[];
  };
}

export interface OrderHistoryResponse {
  pages: number;
  totalOrderCount: number;
  orders: OrderData[];
}

// ============================================================================
// API Functions
// ============================================================================

export const orderApi = {
  /**
   * Get the overall status of orders which are waiting for video upload
   */
  async getOrderVideoUploadStatus(request: EmptyRequest = {}): Promise<OrderVideoUploadStatusResponse> {
    return apiClient.makeAuthenticatedRequest<OrderVideoUploadStatusResponse>("/order/order_video_upload_status", {
      method: "POST",
      params: request,
      retries: READ_RETRIES,
    });
  },

  /**
   * Manually change order to next status. Used for test purpose only. Not available on production
   */
  async changeToNextStatus(request: ChangeToNextStatusRequest): Promise<ChangeToNextStatusResponse> {
    return apiClient.makeAuthenticatedRequest<ChangeToNextStatusResponse>("/order/change_to_next_status", {
      method: "POST",
      params: request,
    });
  },

  /**
   * Fill the shipping and billing addresses for the order
   */
  async fillAddresses(request: FillAddressesRequest): Promise<FillAddressesResponse> {
    return apiClient.makeAuthenticatedRequest<FillAddressesResponse>("/order/fill_addresses", {
      method: "POST",
      params: request,
    });
  },

  /**
   * Check if the user needs to fill the shipping and billing addresses for the order
   */
  async requireFillAddresses(request: RequireFillAddressesRequest): Promise<RequireFillAddressesResponse> {
    return apiClient.makeAuthenticatedRequest<RequireFillAddressesResponse>("/order/require_fill_addresses", {
      method: "POST",
      params: request,
    });
  },

  /**
   * Get the order history
   */
  async getOrderHistory(request: OrderHistoryRequest = {}, page = 1): Promise<OrderHistoryResponse> {
    const history = await apiClient.makeAuthenticatedRequest<OrderHistoryResponse>(`/order/history/${page}`, {
      method: "POST",
      params: request,
      retries: READ_RETRIES,
    });
    return {
      ...history,
      orders: history.orders.map(this.formatOrderData),
    };
  },

  /**
   * Get the order detail
   */
  async getOrder(orderId: string): Promise<OrderData> {
    const order = await apiClient.makeAuthenticatedRequest<OrderData>(`/order/${orderId}`, {
      method: "POST",
      retries: READ_RETRIES,
    });
    return this.formatOrderData(order);
  },

  formatOrderData(order: OrderData): OrderData {
    const orderId = order._id || order.orderId || "";
    return {
      ...order,
      _id: orderId,
      orderId,
    };
  },
};
